import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Loader2, MonitorSmartphone, RefreshCw, ArrowRight } from 'lucide-react';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// Vestaboard character codes → display character / colour chip (mirrors the backend matrix)
const CODE_PUNCT = { 37: '!', 38: '@', 39: '#', 40: '$', 41: '(', 42: ')', 44: '-', 46: '+', 47: '&', 48: '=', 49: ';', 50: ':', 52: "'", 53: '"', 54: '%', 55: ',', 56: '.', 59: '/', 60: '?' };
export const TILE_COLORS = { 63: '#cc3b38', 64: '#e07b2f', 65: '#e6c02f', 66: '#3f9b4f', 67: '#2f5fcf', 68: '#7a3fcf', 69: '#f5f5f5', 70: '#141414', 71: '#e8e8e8' };
export const codeToChar = (code) => {
  if (code >= 1 && code <= 26) return String.fromCharCode(64 + code);
  if (code >= 27 && code <= 36) return '1234567890'[code - 27];
  return CODE_PUNCT[code] || '';
};

// Render one board row from its integer codes: colour codes become solid chips.
export function BoardRow({ codes }) {
  return (
    <div className="flex gap-[2px]">
      {codes.map((code, i) => {
        const chip = TILE_COLORS[code];
        return (
          <span key={i}
            style={chip ? { background: chip } : undefined}
            className={`w-[16px] h-[22px] sm:w-[18px] sm:h-[24px] flex items-center justify-center rounded-[2px] text-[11px] sm:text-xs font-mono leading-none shadow-inner ${chip ? '' : 'bg-neutral-900 text-neutral-100'}`}>
            {chip ? '' : code === 62 ? '♥' : (codeToChar(code) || '\u00A0')}
          </span>
        );
      })}
    </div>
  );
}

const REASON = { rotation: 'Rotation', event: 'Event', manual: 'Push' };

// Compact "what the Note shows right now" — the full control lives in Admin → Vestaboard (Board Manager).
export function VestaboardCard() {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await api.vbManagerStatus(); setSt(r.data); } catch (e) { setSt(null); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-sm p-5 flex items-center gap-2 text-muted-foreground" data-testid="vestaboard-card">
        <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-sm">Vestaboard…</span>
      </div>
    );
  }
  if (!st) return null;
  const cur = st.current;
  const dry = st.settings?.dry_run;

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="vestaboard-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
            <MonitorSmartphone className="w-4 h-4 text-amber-400" /> Vestaboard
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${dry ? 'text-amber-300 border-amber-500/40 bg-amber-500/10' : 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'}`} data-testid="vestaboard-manager-mode">{dry ? 'DRY_RUN' : 'LIVE'}</span>
          </h2>
          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="vestaboard-now-text">
            {cur ? <>Zeigt <span className="text-foreground">{cur.board_name}</span> ({REASON[cur.reason] || cur.reason}) seit {fmt(cur.since)}</> : 'Noch nichts gesendet'}
            {st.queued_events?.length > 0 && <> · {st.queued_events.length} Event{st.queued_events.length > 1 ? 's' : ''} in der Warteschlange</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} title="Neu laden" data-testid="vestaboard-reload-btn"
            className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-border text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <Link to="/vestaboard" data-testid="vestaboard-manager-link"
            className="inline-flex items-center gap-1.5 text-[11px] text-amber-300 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 rounded-sm px-2.5 py-1.5 transition-colors">
            Board Manager <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <div className="flex justify-center">
        <div className="inline-flex flex-col gap-[2px] p-2.5 rounded bg-neutral-950 border border-neutral-800" data-testid="vestaboard-preview">
          {(cur?.matrix || [[], [], []]).map((codes, i) => <BoardRow key={i} codes={codes.length ? codes : Array(15).fill(0)} />)}
        </div>
      </div>
      <p className="text-center text-[10px] text-muted-foreground mt-2">
        Nur Projektnamen, nie Firmennamen · letzter Versand {fmt(st.state?.last_sent_at)}
      </p>
    </div>
  );
}
